import React from 'react'
import { createRoot } from 'react-dom/client'
import PlyrPlayer from './PlyrPlayer.js'
import YouTubeNativePlayer from '../youtube/YouTubeNativePlayer.jsx'

class InlineVideoManager {
  constructor() {
    this.players = new Map()
    this.isInitialized = false
  }

  init() {
    if (this.isInitialized) return
    this.isInitialized = true

    // Initialize existing players
    this.initializeExistingPlayers()

    // Watch for new players (in case of dynamic content)
    this.observeDOM()
  }

  initializeExistingPlayers() {
    const players = document.querySelectorAll('.ey-video-player')
    players.forEach((player, index) => {
      this.initializePlayer(player, index)
    })
  }

  observeDOM() {
    // Use MutationObserver to watch for new video players
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a video player
            if (node.classList && node.classList.contains('ey-video-player')) {
              this.initializePlayer(node, Date.now())
            }
            // Check if the added node contains video players
            const players = node.querySelectorAll ? node.querySelectorAll('.ey-video-player') : []
            players.forEach((player, index) => {
              this.initializePlayer(player, Date.now() + index)
            })
          }
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  initializePlayer(playerElement, id) {
    // Skip if already initialized
    if (playerElement.dataset.initialized === 'true') return

    // Get video data from data attributes
    const videoType = this.getVideoType(playerElement)
    const videoUrl = this.getVideoUrl(playerElement, videoType)
    const poster = playerElement.dataset.poster || ''



    if (!videoUrl) {
      console.warn('No video URL found for player:', playerElement)
      return
    }

    // Mark as initialized
    playerElement.dataset.initialized = 'true'

    // Create container
    const container = document.createElement('div')
    container.className = 'inline-video-player-container'
    playerElement.appendChild(container)

    // For MP4 and Vimeo: use PlyrPlayer (vanilla JS)
    if (videoType === 'mp4' || videoType === 'vimeo') {
      const plyrInstance = new PlyrPlayer(container, {
        url: videoUrl,
        poster: poster,
        type: videoType
      })
      this.players.set(id, { 
        player: plyrInstance, 
        container, 
        element: playerElement,
        type: 'plyr'
      })
      return
    }

    // For YouTube: use React component (native YouTube API)
    const root = createRoot(container)
    
    root.render(
      React.createElement(YouTubeNativePlayer, {
        type: videoType,
        url: videoUrl,
        videoId: videoUrl,
        poster: poster,
        id: id,
        onDestroy: () => {
          this.players.delete(id)
          root.unmount()
        }
      })
    )

    this.players.set(id, { root, container, element: playerElement, type: 'react' })
  }

  getVideoType(playerElement) {
    // Check for specific video type attributes
    if (playerElement.dataset.youtube) return 'youtube'
    if (playerElement.dataset.vimeo) return 'vimeo'
    if (playerElement.dataset.mp4) return 'mp4'

    // Fallback: check for any video type attribute
    const attributes = Array.from(playerElement.attributes)
    for (const attr of attributes) {
      if (attr.name.startsWith('data-') && attr.name !== 'data-poster') {
        return attr.name.replace('data-', '')
      }
    }

    return 'youtube' // Default fallback
  }

  getVideoUrl(playerElement, videoType) {
    switch (videoType) {
      case 'youtube':
        return playerElement.dataset.youtube || playerElement.dataset.videoKey || ''
      case 'vimeo':
        return playerElement.dataset.vimeo || playerElement.dataset.videoKey || ''
      case 'mp4':
        return playerElement.dataset.mp4 || ''
      default:
        return ''
    }
  }

  destroy() {
    this.players.forEach((playerData) => {
      try {
        if (playerData.type === 'plyr' && playerData.player) {
          playerData.player.destroy()
        } else if (playerData.root) {
          playerData.root.unmount()
        }
      } catch (error) {
        console.warn('Error destroying inline video player:', error)
      }
    })
    this.players.clear()
    this.isInitialized = false
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  if (!window.inlineVideoManager) {
    window.inlineVideoManager = new InlineVideoManager()
    window.inlineVideoManager.init()
  }
})

export default InlineVideoManager
